import { BehaviorSubject, catchError, combineLatest, distinctUntilChanged, interval, map, of, startWith, switchMap, tap } from 'rxjs';
import { ChangeDetectionStrategy, Component, Inject, signal, TemplateRef, ViewChild, } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { FormGroup, FormControl } from '@angular/forms';
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { DialogService } from 'primeng/dynamicdialog';
import type { MenuItem } from "primeng/api";
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from '@medsurf/flat-facades';
import * as FlatServices from '@medsurf/flat-services';
import { ModalControlComponent } from '@medsurf/flat-components';
import { FLAT_SETTINGS, type SettingControlModel } from '@medsurf/flat-services';
import { PageLinkDialogComponent } from "../../common/page-link-dialog/page-link-dialog.component";

@Component({
    selector: 'medsurf-import-stats',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './import-stats.component.html',
    styleUrls: ['./import-stats.component.scss'],
    standalone: false
})
export class ImportStatsComponent {
  @ViewChild('log_template') log: TemplateRef<any>;
  
  isLoading = signal(true);
  #handled = false;
  #typedNodeExpanded: { [id: string]: boolean } = {};
  first = 0;
	getSlidesMenuItems = () => {
		const selectPageSet = this.bulkEditAuthorFacade.snapshot_selectPageSet();
		const disabled = selectPageSet.size === 0;
		return [
			{
				label: this.translate.instant('global.deselect'),
				disabled: disabled,
				icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.SELECTION_REMOVE,
				command: () => this.deselectPages(),
			},
			{
				label: this.translate.instant('global.pages_linking'),
				disabled: disabled,
				icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.ATTACH,
				command: () => this.bulkAttachPages(),
			}
		] satisfies MenuItem[];
	}
  readonly userProfile = this.settings.deepzoomProject;
  readonly FlatModels = FlatModels;

  toolbarSettingsForm = new FormGroup({
    searchString: new FormControl(''),
    allProfiles: new FormControl(false),
    autoRefresh: new FormControl(false),
    rows: new FormControl(25)
  });
	
	readonly #refreshInterval = 12 * 1000;
  #searchStringInput$ = this.toolbarSettingsForm.controls.searchString.valueChanges
    .pipe(startWith(this.toolbarSettingsForm.value.searchString));
  #allProfilesInput$ = this.toolbarSettingsForm.controls.allProfiles.valueChanges
    .pipe(startWith(this.toolbarSettingsForm.value.allProfiles));
  #autoRefreshInput$ = this.toolbarSettingsForm.controls.autoRefresh.valueChanges
    .pipe(startWith(this.toolbarSettingsForm.value.autoRefresh));
  // Timestamps
  #manualRefreshTimestamp$ = new BehaviorSubject(Date.now());
  #automaticRefreshTimestamp$ = interval(this.#refreshInterval)
    .pipe(map(() => Date.now()), startWith(Date.now()))
  #updateTimestamp$ = combineLatest([
    this.#automaticRefreshTimestamp$,
    this.#manualRefreshTimestamp$,
    this.#autoRefreshInput$
  ]).pipe(
    map(([t, manualT, autoRefresh]) => autoRefresh ? t : manualT),
    distinctUntilChanged()
  )
  // Data
  #scans$ = this.#updateTimestamp$.pipe(
    tap(() => this.isLoading.set(true)),
    switchMap(t => {
      if (this.settings.deepzoomStatsUrl) {
        return this.httpClient.get<{ scans: FlatModels.ImportStatsModels.ImportStats; }>(
          this.settings.deepzoomStatsUrl,
          { params: { t } }
        )
      }
      return of({scans: {}});
    }),
    map((data) => {
      const validation = FlatModels.ImportStatsModels.ImportStatsSchema.safeParse(data.scans);
      if (validation.error) throw(validation.error);
      return validation.data;
    }),
    map((data) => {
      return data.toSorted((a, b) => {
        return Date.parse(a.lastModified) - Date.parse(b.lastModified);
      })
    }),
    catchError(_ => {
      this.alertControlFacade.requestAddAlert({
        key: 'main',
        detail: 'medsurf-import-stats.error_reading_log',
        severity: FlatModels.AlertControlModels.AlertType.enum.error,
        life: FlatModels.AlertControlModels.AlertTime.DEFAULT,
      });
      return of ([])
    }),
    tap(() => this.isLoading.set(false)),
    map(scans => scans.toReversed()),
  );
  filteredScans$ = combineLatest([
    this.#scans$,
    this.#searchStringInput$,
    this.#allProfilesInput$
  ]).pipe(
    tap(() => this.first = 0),
    map(([scans, searchString, allProfiles]) => {
      let filteredScans = allProfiles ? scans : scans.filter(scan => scan.profile === this.settings.deepzoomProject);
      if (searchString && searchString.length > 0) {
        filteredScans = filteredScans.filter(scan => scan.name.includes(searchString));
      }
      // Load Scan Medias; so we can display a thumbnail
      const mediaIds: string[] = [];
      if (filteredScans.length > 0) {
        for (let i = this.first; i <= (this.first + (this.toolbarSettingsForm.value.rows || 10)); i++) {
          if (i >= filteredScans.length) break;
          const entry = filteredScans[i]?.media;
          if (entry && entry[0]) mediaIds.push(entry[0].id);
        }
      }
      this.mediaAuthorFacade.requestGetMedia(mediaIds);
      return filteredScans;
    }),
  );
  
  constructor(private alertControlFacade: FlatFacades.AlertControlFacade,
              private mediaAuthorFacade: FlatFacades.MediaAuthorFacade,
              private bulkEditAuthorFacade: FlatFacades.BulkEditAuthorFacade,
              public slideAuthorFacade: FlatFacades.SlideAuthorFacade,
              public nodeSharedFacade: FlatFacades.NodeSharedFacade,
              public historySharedFacade: FlatFacades.HistorySharedFacade,
              public mediaControlService: FlatServices.MediaControlService,
              private dialogService: DialogService,
              private translate: TranslateService,
              private httpClient: HttpClient,
              private router: Router,
              @Inject(FLAT_SETTINGS) private settings: SettingControlModel) {
    this.slideAuthorFacade.typedNodeExpanded$.pipe(takeUntilDestroyed()).subscribe((typedNodeExpanded) => {
      this.#typedNodeExpanded = typedNodeExpanded;
    });
  }
  
  getStatusSeverityClass(status: FlatModels.ImportStatsModels.StatusSeverity) {
    return (FlatModels.ImportStatsModels.StatusSeverityClass[status] || "unknown") as "success" | "info" | "warning" | "danger" | "secondary" | "contrast";
  }
  
  getM(log: any) {
    return log.m;
  }
  
  refresh() {
    this.#manualRefreshTimestamp$.next(Date.now());
  }
  
  onPageClick(event: MouseEvent,
              statsData: FlatModels.ImportStatsModels.ImportStatsEntry) {
    if (this.#handled || !statsData.slideId) {
      this.#handled = false;
      return;
    }
    document.getSelection()?.removeAllRanges();
    if (event.ctrlKey || event.metaKey) {
      return this.bulkEditAuthorFacade.requestTogglePage(statsData.slideId).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
      });
    } else {
      const headerTitle = `${statsData.name}`;
      this.dialogService.open(ModalControlComponent, {
        header: headerTitle,
        data: {
          statsData,
          template: this.log,
        },
        dismissableMask: true,
        width: '70%',
        style: { 'height': 'auto', 'max-height': '70vh' },
      });
    }
  }
  
  onPage(event: any,
         filteredScans: FlatModels.ImportStatsModels.ImportStatsEntry[]) {
    this.first = event.first;
    // Load Scan Medias; so we can display a thumbnail
    const mediaIds: string[] = [];
    if (filteredScans.length > 0) {
      for (let i = this.first; i <= (this.first + (this.toolbarSettingsForm.value.rows || 10)); i++) {
        if (i >= filteredScans.length) break;
        const entry = filteredScans[i]?.media;
        if (entry && entry[0]) mediaIds.push(entry[0].id);
      }
    }
    this.mediaAuthorFacade.requestGetMedia(mediaIds);
  }
  
  trackBy(index: number,
          item: FlatModels.ImportStatsModels.ImportStatsEntry) {
    return item.id || index;
  }
  
  goToSlide(id: string) {
    this.#handled = true;
    const url: string = this.router.serializeUrl(this.router.createUrlTree(['slide', id]));
    window.open(url, '_blank');
  }
  
  changeLayout(event: any) {
    this.slideAuthorFacade.requestSetLayout(event.layout).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  onToggleBulkPage(id: string) {
    this.#handled = true;
    this.bulkEditAuthorFacade.requestTogglePage(id).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  attachPage(id: string) {
    this.#handled = true;
    this.bulkEditAuthorFacade.requestSetPages([id]).subscribe(() => {
      const dialogRef = this.dialogService.open(PageLinkDialogComponent, {
        header: this.translate.instant('global.pages_link_title'),
        dismissableMask: true,
        width: '70%',
        height: '90%',
      });
      dialogRef.onClose.subscribe(data => {
        if (data?.result) {
          this.bulkEditAuthorFacade.requestAttachSelectedPagesToNodes(data.nodes).subscribe({
            error: (result) => this.alertControlFacade.requestAddError(result),
            next: () => {
              for(const node of data.nodes) {
                this.#typedNodeExpanded[node.id] = true;
                this.slideAuthorFacade.requestSetTypedNodeExpanded(this.#typedNodeExpanded).subscribe({
                  error: (result) => this.alertControlFacade.requestAddError(result)
                });
              }
              this.alertControlFacade.requestAddAlert({
                key: 'main',
                detail: 'notification.slide_linked',
                severity: FlatModels.AlertControlModels.AlertType.enum.success,
                life: FlatModels.AlertControlModels.AlertTime.DEFAULT
              });
            }
          });
        }
      });
    });
  }
  
  private deselectPages() {
    this.bulkEditAuthorFacade.requestDeselectPages().subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  private bulkAttachPages() {
	  const dialogRef = this.dialogService.open(PageLinkDialogComponent, {
		  header: this.translate.instant('global.pages_link_title'),
		  dismissableMask: true,
		  width: '70%',
		  height: '90%',
	  });
	  dialogRef.onClose.subscribe(data => {
		  if (data && data.result) {
			  this.bulkEditAuthorFacade.requestAttachSelectedPagesToNodes(data.nodes).subscribe({
				  error: (result) => this.alertControlFacade.requestAddError(result),
				  next: () => {
					  for(const node of data.nodes) {
						  this.#typedNodeExpanded[node.id] = true;
						  this.slideAuthorFacade.requestSetTypedNodeExpanded(this.#typedNodeExpanded).subscribe({
							  error: (result) => this.alertControlFacade.requestAddError(result),
						  });
					  }
					  this.alertControlFacade.requestAddAlert({
						  key: 'main',
						  detail: 'notification.slides_linked',
						  severity: FlatModels.AlertControlModels.AlertType.enum.success,
						  life: FlatModels.AlertControlModels.AlertTime.DEFAULT
					  });
				  }
			  });
		  }
	  });
  }
  
  executeUndo() {
    if (this.historySharedFacade.snapshot_canUndo()) {
      this.historySharedFacade.requestUndoSnapshot().subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
      });
    }
  }
  
  executeRedo() {
    if (this.historySharedFacade.snapshot_canRedo()) {
      this.historySharedFacade.requestRedoSnapshot().subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
      });
    }
  }
  
  executeSave() {
    if (this.historySharedFacade.snapshot_canSave()) {
      this.historySharedFacade.requestSaveSnapshots().subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
        next: () => {
          this.alertControlFacade.requestAddAlert({
            key: 'main',
            detail: 'notification.saved',
            severity: FlatModels.AlertControlModels.AlertType.enum.success,
            life: FlatModels.AlertControlModels.AlertTime.DEFAULT
          });
        }
      });
    }
  }
  
  openNodePreview(routeElement: FlatModels.NodeEntityModels.RouteElement) {
    if (this.historySharedFacade.snapshot_canSave()) {
      this.historySharedFacade.requestSaveSnapshots().subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
        next: () => {
          this.alertControlFacade.requestAddAlert({
            key: 'main',
            detail: 'notification.saved',
            severity: FlatModels.AlertControlModels.AlertType.enum.success,
            life: FlatModels.AlertControlModels.AlertTime.DEFAULT
          });
          // Navigate To Url
          const url = `${this.settings.viewerUrl}${routeElement.path}`;
          open(url, '_medsurf_preview');
        }
      });
    } else {
      // Navigate To Url
      const url = `${this.settings.viewerUrl}${routeElement.path}`;
      open(url, '_medsurf_preview');
    }
  }
}
